const META_KEYS = new Set(['Form', 'Entry', 'Id', 'Section'])

const ADDRESS_KEYS = new Set([
  'Line1',
  'Line2',
  'Line3',
  'City',
  'State',
  'PostalCode',
  'Country',
  'CountryCode',
  'Type',
  'StreetAddress',
  'FullAddress',
  'FullInternationalAddress',
  'CityStatePostalCode',
  'Latitude',
  'Longitude',
])

const ADDRESS_DISPLAY_KEYS = ['Line1', 'Line2', 'Line3', 'City', 'State', 'PostalCode', 'Country', 'Type']

const LONG_TEXT_THRESHOLD = 120

export const humanizeKey = key => {
  if (!key || typeof key !== 'string') return ''
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, c => c.toUpperCase())
}

export const shouldSkipKey = (key, value) => {
  if (!key) return true
  if (META_KEYS.has(key)) return true
  if (key.endsWith('_IsRequired')) return true
  if (key === 'Id' || key === 'ItemNumber') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

export const isRequired = (parent, key) => {
  if (!parent || typeof parent !== 'object') return false
  return parent[`${key}_IsRequired`] === true
}

export const isPlainObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)

export const isAddressObject = value => {
  if (!isPlainObject(value)) return false
  const keys = Object.keys(value)
  if (!keys.length) return false
  const addressHits = keys.filter(k => ADDRESS_KEYS.has(k)).length
  return addressHits >= 2
}

export const isIdLabel = value => typeof value === 'string' && /^\d+-[A-Za-z]/.test(value)

export const isDateLike = value => {
  if (typeof value !== 'string') return false
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return true
  if (/^\d{2}\/\d{2}\/\d{2,4}$/.test(value)) return true
  return false
}

export const formatIdLabel = value => {
  if (typeof value !== 'string') return value
  const idx = value.indexOf('-')
  if (idx === -1) return value
  return value.slice(idx + 1)
}

export const formatDisplayValue = value => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (isIdLabel(value)) return formatIdLabel(value)
  if (typeof value === 'number') return String(value)
  return String(value)
}

export const inferFieldType = (key, value) => {
  if (typeof value === 'boolean') return 'boolean'
  if (Array.isArray(value)) {
    if (!value.length) return 'empty'
    if (value.every(item => typeof item === 'string')) return 'checkboxGroup'
    if (value.every(item => isPlainObject(item))) return 'repeating'
    return 'list'
  }
  if (isAddressObject(value)) return 'address'
  if (isPlainObject(value)) return 'group'
  if (typeof value === 'string') {
    if (key === 'Text' || value.length >= LONG_TEXT_THRESHOLD) return 'textarea'
    if (isDateLike(value)) return 'date'
    if (isIdLabel(value)) return 'select'
    return 'text'
  }
  if (typeof value === 'number') return 'text'
  return 'text'
}

export const getVisibleEntries = obj => {
  if (!isPlainObject(obj)) return []
  return Object.entries(obj).filter(([key, value]) => !shouldSkipKey(key, value))
}

export const getAddressEntries = obj => {
  return ADDRESS_DISPLAY_KEYS.filter(key => Object.prototype.hasOwnProperty.call(obj, key)).map(key => [key, obj[key] ?? ''])
}

export const isHalfWidthType = type => ['text', 'date', 'select', 'boolean'].includes(type)

export const groupEntriesIntoRows = (entries, parent) => {
  const rows = []
  let halfBuffer = []

  const flushHalf = () => {
    if (!halfBuffer.length) return
    rows.push({ kind: 'fields', fields: halfBuffer })
    halfBuffer = []
  }

  entries.forEach(([key, value]) => {
    const type = inferFieldType(key, value)
    const field = {
      key,
      value,
      type,
      label: humanizeKey(key),
      required: isRequired(parent, key),
    }

    if (type === 'group' || type === 'address' || type === 'repeating' || type === 'checkboxGroup' || type === 'textarea' || type === 'list') {
      flushHalf()
      rows.push({ kind: 'block', field })
      return
    }

    if (isHalfWidthType(type)) {
      halfBuffer.push(field)
      if (halfBuffer.length === 2) flushHalf()
      return
    }

    flushHalf()
    rows.push({ kind: 'fields', fields: [field] })
  })

  flushHalf()
  return rows
}
